import { format, isToday, isYesterday } from 'date-fns';

// ── Currency ────────────────────────────────────────────────────────

/**
 * Format a number as a currency string.
 *
 * Backward-compatible signature: the second positional arg is `fractionDigits`
 * for legacy callers. A third arg (or an `opts` object) lets new callers pass
 * a currency code. Defaults to USD so existing call sites don't change.
 *
 * @example
 *   formatCurrency(45.99)                      // "$45" (0 fraction digits default)
 *   formatCurrency(45.99, 2)                   // "$45.99"
 *   formatCurrency(45.99, 2, 'GBP')            // "£45.99"
 *   formatCurrency(45.99, { currency: 'GBP' }) // "£45.99"
 */
export function formatCurrency(
  value: number | null,
  fractionDigitsOrOpts: number | { currency?: string; fractionDigits?: number } = 0,
  currencyArg?: string,
): string {
  if (value === null) return '—';
  let fractionDigits: number;
  let currency: string;
  if (typeof fractionDigitsOrOpts === 'number') {
    fractionDigits = fractionDigitsOrOpts;
    currency = currencyArg ?? 'USD';
  } else {
    fractionDigits = fractionDigitsOrOpts.fractionDigits ?? 0;
    currency = fractionDigitsOrOpts.currency ?? currencyArg ?? 'USD';
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  } catch {
    // Unknown currency code — fall back to USD.
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(value);
  }
}

/**
 * Format a number with an explicit sign prefix (`+`, `−`, or none).
 * Useful in ledgers and reports where direction matters at a glance.
 */
export function formatSignedCurrency(
  value: number,
  currency = 'USD',
  fractionDigits?: number,
): string {
  const abs = Math.abs(value);
  const digits = fractionDigits ?? (abs % 1 === 0 ? 0 : 2);
  let formatted: string;
  try {
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(abs);
  } catch {
    // Unknown currency code — fall back to USD format.
    formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(abs);
  }
  if (value < 0) return `−${formatted}`;
  if (value > 0) return `+${formatted}`;
  return formatted;
}

// ── Date / Time ─────────────────────────────────────────────────────

/**
 * Format a date string as date + time.
 * Default format: "MMM d, yyyy HH:mm" (e.g. "Apr 15, 2026 14:30").
 */
export function formatDateTime(
  dateStr: string | Date,
  fmt = 'MMM d, yyyy HH:mm',
): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  // Guard against unparseable inputs (e.g. a Xero `/Date(ms)/` string or empty
  // value) — date-fns `format` throws `RangeError: Invalid time value`, which
  // would white-screen the whole app. Degrade to a dash instead.
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return format(d, fmt);
}

/**
 * Format a date string as date only.
 * Default format: "MMM d, yyyy" (e.g. "Apr 15, 2026").
 */
export function formatDate(
  dateStr: string | Date,
  fmt = 'MMM d, yyyy',
): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  return format(d, fmt);
}

/**
 * Smart relative time for conversation lists and activity feeds.
 * - Today → "14:30"
 * - Yesterday → "Yesterday"
 * - This year → "Apr 15"
 * - Older → "Apr 15, 2025"
 */
export function formatRelativeTime(dateStr: string | Date): string {
  const d = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '—';
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  if (d.getFullYear() === new Date().getFullYear()) return format(d, 'MMM d');
  return format(d, 'MMM d, yyyy');
}

/**
 * Format month and year from numeric values.
 * Default format: "MMMM yyyy" (e.g. "March 2026").
 */
export function formatMonth(
  year: number,
  month: number,
  fmt = 'MMMM yyyy',
): string {
  return format(new Date(year, month - 1), fmt);
}
